import React, { useState } from 'react';
import {
    Box,
    Textarea,
    Button,
    Text,
    Alert,
    Collapse,
    Paper,
    ActionIcon,
    Tooltip,
    Group
} from '@mantine/core';
import {
    IconClipboard,
    IconX,
    IconCheck,
    IconAlertCircle,
    IconPlus,
    IconMinus
} from '@tabler/icons-react';
import { FilterGroup } from './ScimFilterBuilder';
import { parseScimFilter, validateScimFilter } from './ScimFilterParser';

export interface ScimFilterManualInputProps {
    onFilterParsed: (filterGroup: FilterGroup) => void;
    onClear?: () => void;
    placeholder?: string;
    label?: string;
    helperText?: string;
    disabled?: boolean;
    /** Controlled value (when provided, parent owns the filter string). */
    value?: string;
    /** Called when the filter string changes (use with value for controlled mode). */
    onChange?: (value: string) => void;
    /** Called when user presses Enter in the textarea (e.g. to apply filter and search). */
    onApplyEnter?: () => void;
}

export const ScimFilterManualInput: React.FC<ScimFilterManualInputProps> = ({
    onFilterParsed,
    onClear,
    placeholder = 'Type or paste a SCIM filter here...',
    label = 'SCIM Filter',
    helperText = 'Type or paste a SCIM filter string. Use the button below to populate the filter builder. For Group, you can filter by property counts in manual mode (e.g. membertransitive $count.gt 999).',
    disabled = false,
    value: controlledValue,
    onChange: onControlledChange,
    onApplyEnter
}) => {
    const [internalFilterString, setInternalFilterString] = useState('');
    const isControlled = controlledValue !== undefined;
    const filterString = isControlled ? controlledValue : internalFilterString;
    const [validationResult, setValidationResult] = useState<{
        isValid: boolean;
        error?: string;
    } | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleFilterChange = (value: string) => {
        if (!isControlled) {
            setInternalFilterString(value);
        }
        onControlledChange?.(value);

        if (value.trim()) {
            const validation = validateScimFilter(value);
            setValidationResult(validation);
        } else {
            setValidationResult(null);
        }
    };

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            handleFilterChange(text);
        } catch (error) {
            console.error('Failed to read clipboard:', error);
        }
    };

    const handleApply = () => {
        if (!filterString.trim()) return;

        const parsedFilter = parseScimFilter(filterString);
        if (parsedFilter) {
            onFilterParsed(parsedFilter);
            if (!isControlled) {
                setInternalFilterString('');
            }
            setValidationResult(null);
            setIsExpanded(false);
        }
    };

    const handleClear = () => {
        if (!isControlled) {
            setInternalFilterString('');
        }
        setValidationResult(null);
        setIsExpanded(false);
        onClear?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onApplyEnter?.();
        }
    };

    const handleBlur = () => {
        const trimmed = filterString.trim();
        if (trimmed !== filterString) handleFilterChange(trimmed);
    };

    const isValidFilter = validationResult?.isValid === true;
    const hasError = validationResult?.isValid === false;

    return (
        <Paper withBorder p="md" mb="md">
            <Group align="center" gap="xs" mb="xs">
                <Text fw={600} size="lg" component="h3">
                    {label}
                </Text>
                <Tooltip label="Paste from clipboard">
                    <span>
                        <ActionIcon
                            onClick={handlePasteFromClipboard}
                            disabled={disabled}
                            size="sm"
                        >
                            <IconClipboard size={16} />
                        </ActionIcon>
                    </span>
                </Tooltip>
            </Group>

            <Textarea
                w="100%"
                minRows={isExpanded ? 4 : 2}
                value={filterString}
                onChange={(e) => handleFilterChange(e.currentTarget.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                description={helperText}
                disabled={disabled}
                error={hasError}
                rightSectionPointerEvents={filterString ? 'all' : undefined}
                rightSection={
                    filterString ? (
                        <Group gap="xs">
                            <Tooltip label="Clear">
                                <ActionIcon
                                    onClick={handleClear}
                                    size="sm"
                                >
                                    <IconX size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Toggle expanded view">
                                <ActionIcon
                                    onClick={() => setIsExpanded(!isExpanded)}
                                    size="sm"
                                >
                                    {isExpanded ? <IconMinus size={16} /> : <IconPlus size={16} />}
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    ) : undefined
                }
            />

            <Collapse in={!!validationResult}>
                {validationResult && (
                    <Box mt="xs">
                        {isValidFilter ? (
                            <Alert
                                color="green"
                                icon={<IconCheck size={16} />}
                            >
                                <Group justify="space-between" wrap="nowrap">
                                    <Text size="sm">
                                        Valid SCIM filter. Click "Apply Filter" to populate the builder.
                                    </Text>
                                    <Button
                                        variant="outline"
                                        size="xs"
                                        color="dark"
                                        onClick={handleApply}
                                        disabled={disabled}
                                    >
                                        Apply Filter
                                    </Button>
                                </Group>
                            </Alert>
                        ) : (
                            <Alert
                                color="red"
                                icon={<IconAlertCircle size={16} />}
                            >
                                Invalid SCIM filter: {validationResult.error}
                            </Alert>
                        )}
                    </Box>
                )}
            </Collapse>

            {filterString && !validationResult && (
                <Box mt="xs">
                    <Text size="sm" c="dimmed">
                        Type or paste a valid SCIM filter to see validation...
                    </Text>
                </Box>
            )}
        </Paper>
    );
};
